import { useCallback, useEffect, useMemo, useState } from 'react'

import { TradeExternalAccountScreen } from '@pages/Trade/shared/components/TradeExternalAccountScreen'
import { Account } from '@pages/Trade/types'
import { FAVORITES, TAG_TO_ASSET_CLASS, TAG_TO_TYPE } from '@pages/Trade/utils/constants'

import { useDebouncedValue } from '@utils/debounce'

import { DxTradePosition, InstrumentActionHandler, WatchlistModel } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import Filters from './Filters'
import InstrumentsTable from './InstrumentsTable'
import { WatchlistEmptyState } from './Watchlists/WatchlistEmptyState'
import { useDxtradeInstrumentFeed } from './hooks/useDxtradeInstrumentFeed'

const INSTRUMENTS_PAGE_SIZE = 8

type InstrumentsProps = {
  account?: Account
  accountCode: string
  accountCurrency?: CurrencyType
  ownerExternalId?: string
  openPositions?: DxTradePosition[]
  selectedInstrumentSymbol?: string
  onInstrumentSelect?: (symbol: string, priceIncrement?: number | null) => void
  onInstrumentAction?: InstrumentActionHandler
  showSearch?: boolean
  isSearchOpen?: boolean
  searchValue: string
  onSearchChange: (value: string) => void
  collapsed?: boolean
  onToggleCollapse: (collapsed: boolean) => void
  isOrderWidgetOpen?: boolean
  watchlists: WatchlistModel[]
  privateWatchlists: WatchlistModel[]
  favoriteSymbols: string[]
  onToggleFavorite: (symbol: string) => void
  onDefaultSymbol?: (symbol: string, priceIncrement?: number | null) => void
  onFilterReset?: () => void
}

const Instruments = (props: InstrumentsProps) => {
  const {
    account,
    accountCode,
    // accountCurrency,
    ownerExternalId,
    selectedInstrumentSymbol,
    onInstrumentSelect,
    onInstrumentAction,
    showSearch = true,
    isSearchOpen = false,
    searchValue,
    onSearchChange,
    collapsed,
    onToggleCollapse,
    isOrderWidgetOpen,
    watchlists,
    privateWatchlists,
    favoriteSymbols,
    onToggleFavorite,
    onDefaultSymbol,
    onFilterReset,
    // openPositions,
  } = props

  const [activeTag, setActiveTag] = useState<string | null>(null)

  const popularWatchlist = useMemo(
    () => watchlists.find((w) => w.type === 'PUBLIC' && w.name === 'Top 50'),
    [watchlists]
  )

  const activeWatchlist = useMemo(() => {
    if (!activeTag) return undefined
    return popularWatchlist?.name === activeTag
      ? popularWatchlist
      : privateWatchlists.find((w) => w.name === activeTag)
  }, [activeTag, popularWatchlist, privateWatchlists])

  const activeWatchlistSymbols = useMemo(
    () => (activeWatchlist?.symbols ?? []).filter((s) => s != null),
    [activeWatchlist]
  )

  const sanitizedSearchValue = searchValue.replace(/\//g, '')
  const debouncedSearchValue = useDebouncedValue(sanitizedSearchValue.trim(), 400)
  const searchValueWithWildcard = useMemo(() => {
    if (!debouncedSearchValue) {
      return ''
    }

    return `*${debouncedSearchValue}*`
  }, [debouncedSearchValue])

  const assetClassFilter = useMemo(
    () => (activeTag ? TAG_TO_ASSET_CLASS[activeTag] : undefined),
    [activeTag]
  )

  const typeFilter = useMemo(() => (activeTag ? TAG_TO_TYPE[activeTag] : undefined), [activeTag])

  const isFavoritesFilterActive = useMemo(() => activeTag === FAVORITES, [activeTag])
  const isWatchlistFilterActive = isFavoritesFilterActive || !!activeWatchlist

  const isSearching = debouncedSearchValue.length > 0

  const effectiveSymbolFilter = useMemo(() => {
    if (isSearching) {
      return searchValueWithWildcard || undefined
    }

    if (isFavoritesFilterActive) {
      if (!favoriteSymbols.length) return undefined
      return favoriteSymbols.join(',')
    }

    if (activeWatchlist) {
      if (!activeWatchlistSymbols.length) return undefined
      return activeWatchlistSymbols.join(',')
    }

    return undefined
  }, [
    isSearching,
    searchValueWithWildcard,
    isFavoritesFilterActive,
    favoriteSymbols,
    activeWatchlist,
    activeWatchlistSymbols,
  ])

  const effectiveAssetClassFilter = useMemo(() => assetClassFilter, [assetClassFilter])

  const effectiveTypeFilters = useMemo(() => (typeFilter ? [typeFilter] : undefined), [typeFilter])

  const instrumentFeedOptions = useMemo(
    () => ({
      limit: INSTRUMENTS_PAGE_SIZE,
      symbols: effectiveSymbolFilter,
      symbolDescription: isSearching ? debouncedSearchValue : undefined,
      assetClass: isSearching ? undefined : effectiveAssetClassFilter,
      types: isSearching ? undefined : effectiveTypeFilters,
    }),
    [
      effectiveSymbolFilter,
      isSearching,
      debouncedSearchValue,
      effectiveAssetClassFilter,
      effectiveTypeFilters,
    ]
  )

  const isPrivateWatchlistFilterActive = !!activeWatchlist && activeWatchlist.type === 'PRIVATE'
  const skipFetch =
    !isSearching &&
    ((isFavoritesFilterActive && (!favoriteSymbols.length || !effectiveSymbolFilter)) ||
      (isWatchlistFilterActive && (!activeWatchlistSymbols.length || !effectiveSymbolFilter)))

  const watchlistEmptyState = useMemo(() => {
    const emptyWatchlistName =
      (isFavoritesFilterActive && !favoriteSymbols.length && FAVORITES) ||
      (isPrivateWatchlistFilterActive && !activeWatchlistSymbols.length && activeWatchlist?.name) ||
      undefined

    if (!emptyWatchlistName) return undefined

    const emptyWatchlist = isFavoritesFilterActive
      ? privateWatchlists.find((w) => w.name === FAVORITES)
      : activeWatchlist

    return (
      <WatchlistEmptyState
        watchlistName={emptyWatchlistName}
        accountCode={accountCode}
        ownerExternalId={ownerExternalId}
        favoriteSymbols={favoriteSymbols}
        watchlistId={emptyWatchlist?.id}
      />
    )
  }, [
    isFavoritesFilterActive,
    favoriteSymbols,
    isPrivateWatchlistFilterActive,
    activeWatchlistSymbols,
    activeWatchlist,
    privateWatchlists,
  ])

  const { instruments, isInitialLoading, isLoadingMore, hasNext, loadNext, error } =
    useDxtradeInstrumentFeed(skipFetch ? undefined : accountCode, instrumentFeedOptions)

  const handleToggleTag = useCallback(
    (tag: string) => {
      if (tag === 'All') {
        setActiveTag(null)
        onFilterReset?.()
        return
      }

      setActiveTag(tag)
    },
    [onFilterReset]
  )

  const handleWatchlistEdited = useCallback((newName: string) => {
    setActiveTag(newName)
  }, [])

  const handleWatchlistDeleted = useCallback(
    (name: string) => {
      setActiveTag((prev) => (prev === name ? popularWatchlist?.name ?? null : prev))
    },
    [popularWatchlist]
  )

  useEffect(() => {
    if (!activeTag && popularWatchlist?.name) {
      setActiveTag(popularWatchlist.name)
    }
  }, [activeTag, popularWatchlist])

  useEffect(() => {
    const [firstInstrument] = instruments
    const firstSymbol = firstInstrument?.symbol

    if (firstSymbol) {
      onDefaultSymbol?.(firstSymbol, firstInstrument?.priceIncrement)
    }
  }, [instruments, onDefaultSymbol])

  if (error && account) {
    return <TradeExternalAccountScreen account={account} isError />
  }

  return (
    <Box
      flex
      direction='column'
      gap='x16'
      fullHeight
      minHeight='0'
      data-tour-target='trade.instrumentList'
    >
      <Filters
        searchValue={searchValue}
        activeTag={activeTag}
        privateWatchlists={privateWatchlists}
        popularWatchlist={popularWatchlist}
        ownerExternalId={ownerExternalId}
        accountCode={accountCode}
        onWatchlistCreated={handleToggleTag}
        onWatchlistEdited={handleWatchlistEdited}
        onWatchlistDeleted={handleWatchlistDeleted}
        showSearch={showSearch}
        isSearchOpen={isSearchOpen}
        onSearchChange={onSearchChange}
        onToggleTag={handleToggleTag}
      />

      <InstrumentsTable
        accountCode={accountCode}
        instruments={instruments}
        isInitialLoading={isInitialLoading}
        isLoadingMore={isLoadingMore}
        hasNext={hasNext}
        loadNext={loadNext}
        selectedSymbol={selectedInstrumentSymbol}
        onSelect={onInstrumentSelect}
        onInstrumentAction={onInstrumentAction}
        favoriteSymbols={favoriteSymbols}
        onToggleFavorite={onToggleFavorite}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        isOrderWidgetOpen={isOrderWidgetOpen}
        watchlistEmptyState={watchlistEmptyState}
      />
    </Box>
  )
}

export default Instruments
